import { getYouTubeId, youTubeEmbedUrl } from "@/utils/constants";
import { AnimatePresence, motion } from "framer-motion";
import dynamic from "next/dynamic";
import type { CSSProperties, ReactNode } from "react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

const MuxPlayer = dynamic(() => import("@mux/mux-player-react"), {
  ssr: false,
  loading: () => null,
});

interface IHoverVideoProps {
  /** Mux playback id of the silent preview clip (and of the full video when there is no href). */
  playbackId?: string | null;
  /** Link opened by the play badge; YouTube links open in a lightbox, others in a new tab. */
  href?: string | null;
  /** Rendered as the resting state, e.g. a `next/image` of the person's photo. */
  poster: ReactNode;
  title?: string;
  className?: string;
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

/**
 * Photo first, video on hover. Mounts a muted, looping Mux player only while the
 * pointer is over it (never on touch devices), so a row of these stays calm. A
 * small play badge signals that a full video exists and opens it in a lightbox.
 */
export function HoverVideo(props: IHoverVideoProps) {
  const { playbackId, href, poster, title, className } = props;
  const youtubeId = href ? getYouTubeId(href) : null;
  const canOpen = Boolean(href || playbackId);

  const [canHover, setCanHover] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const update = () => setCanHover(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const previewing = canHover && hovering && !!playbackId && !open;

  const onOpen = (e: React.MouseEvent) => {
    // The Team slider navigates on card click; the badge must not do both.
    e.stopPropagation();
    e.preventDefault();
    if (href && !youtubeId) {
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }
    setHovering(false);
    setOpen(true);
  };

  return (
    <div
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      className={`relative overflow-hidden ${className ?? ""}`}
    >
      {poster}

      {previewing && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35 }}
          className="absolute inset-0 pointer-events-none"
        >
          <MuxPlayer
            streamType="on-demand"
            playbackId={playbackId!}
            autoPlay
            muted
            loop
            playsInline
            thumbnailTime={0.1}
            disableCookies
            preload="auto"
            className="absolute inset-0 w-full h-full"
            style={
              {
                ["--media-object-fit" as never]: "cover",
                ["--controls" as never]: "none",
              } as CSSProperties
            }
            title={title ?? ""}
          />
        </motion.div>
      )}

      {canOpen && (
        <button
          type="button"
          onClick={onOpen}
          aria-label={title ? `Bekijk de video van ${title}` : "Bekijk de video"}
          className="absolute bottom-3 left-3 z-10 flex items-center gap-2 bg-orange py-2 px-4 font-display text-sm text-white shadow-lg hover:scale-105 transition-transform active:scale-100"
        >
          <PlayIcon className="w-3.5 h-3.5 fill-white" />
          Bekijk video
        </button>
      )}

      {open && (
        <VideoLightbox
          youtubeId={youtubeId}
          playbackId={youtubeId ? null : playbackId}
          title={title}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

function VideoLightbox({
  youtubeId,
  playbackId,
  title,
  onClose,
}: {
  youtubeId: string | null;
  playbackId?: string | null;
  title?: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  // Portal: slider slides are transformed, which would trap `fixed` inside them.
  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 30 }}
          onClick={(e) => e.stopPropagation()}
          className={`relative bg-black shadow-2xl ${
            youtubeId
              ? "w-full max-w-5xl aspect-video"
              : "w-[min(90vw,60vh)] h-[85vh]"
          }`}
        >
          {youtubeId ? (
            <iframe
              src={youTubeEmbedUrl(youtubeId)}
              title={title ?? "YouTube video"}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full border-0"
            />
          ) : (
            playbackId && (
              <MuxPlayer
                streamType="on-demand"
                playbackId={playbackId}
                autoPlay
                playsInline
                thumbnailTime={0.1}
                disableCookies
                className="absolute inset-0 w-full h-full"
                style={
                  { ["--media-object-fit" as never]: "contain" } as CSSProperties
                }
                title={title ?? ""}
              />
            )
          )}
        </motion.div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="Sluiten"
          className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 font-display text-xl text-white hover:bg-white/20 hover:scale-110 transition-all active:scale-100"
        >
          ✕
        </button>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}
